import { memo, useLayoutEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import startCase from 'lodash-es/startCase';
import { InfiniteQueryObserverResult, UseInfiniteQueryResult } from 'react-query';
import { Chips } from '@frontend/chips';
import { EmptyStateConfig, InfinitePaginatedList, RenderListItemProps } from '@frontend/components';
import { ActionsUI } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { ScopedLocationFilterMenu } from '@frontend/location-filter-menu';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { CallDetailsContext, PhonesUtils, RecentCallRecord, RecentCallRecords } from '@frontend/phone-panels';
import { Photos } from '@frontend/photos';
import { useAppScopeStore } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import {
  BaseFieldProps,
  Button,
  SearchField,
  useFormField,
  useModalControl,
  Text,
  PhoneOutgoingIcon,
  Chip,
} from '@frontend/design-system';
import { CallRecordsFiltersType } from '../../hooks/use-phone-config-state-store';
import { getActionIcon } from '../../ui-utils';
import { Action, CustomActionCell } from '../all-calls/shared-buttons';
import { RecentCallsTrayFilter } from './calls-filter';
import { HistoryActionsGetterProps } from './history-actions';

export const SmallView = memo(
  ({
    getHistoryActions,
    selectedLocationIds,
    setFilters,
    filters,
    infiniteQueryProps,

    defaultFilters,
    hasFilterChanged,

    searchHandler,
    listHeight,
    panelProps,
  }: {
    getHistoryActions: (props: HistoryActionsGetterProps) => Action[];
    selectedLocationIds: string[];
    setFilters: (filters: CallRecordsFiltersType) => void;
    filters: CallRecordsFiltersType;
    infiniteQueryProps: UseInfiniteQueryResult<RecentCallRecords, unknown>;

    defaultFilters: CallRecordsFiltersType;
    hasFilterChanged: (incomingFilters: CallRecordsFiltersType, baselineFilters?: CallRecordsFiltersType) => boolean;

    searchHandler: (searchText: string) => void;
    emptyState: EmptyStateConfig;

    listHeight?: number;
    panelProps: PanelProps;
  }) => {
    const { t } = useTranslation('calls');
    const searchField = useFormField(
      {
        type: 'text',
        value: filters.name,
        placeholder: t('Search name or 10-digit number'),
      },
      [filters.name]
    );

    const [shouldShowSearch, setShouldShowSearch] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const [callActionContext, setCallActionContext] = useState<{
      personId: string;
      phoneNumber: string;
      outboundNumber: string;
    }>({
      personId: '',
      phoneNumber: '',
      outboundNumber: '',
    });

    const { triggerProps: callTriggerProps, Modal: PhoneCallModal } = ActionsUI.actions.usePhoneCallAction({
      context: callActionContext,
    });

    const allDataRows = useMemo(() => {
      if (!infiniteQueryProps.data?.pages) return [];
      return infiniteQueryProps.data.pages.flatMap((page) => page.data || []);
    }, [infiniteQueryProps]);

    const renderListItem = useMemo(
      () =>
        getRenderListItem({
          getHistoryActions,
          triggerCall: (context) => {
            setCallActionContext({
              personId: context.personId,
              phoneNumber: context.phoneNumber,
              outboundNumber: context.outboundNumber,
            });
            callTriggerProps?.onClick();
          },
          setSelectedIndex,
          shouldShowLocationBadge: selectedLocationIds.length > 1,
          panelProps: {
            ...panelProps,
            rows: allDataRows,
            getNextPage: infiniteQueryProps.fetchNextPage,
          },
        }),
      [
        getHistoryActions,
        selectedLocationIds.length,
        panelProps,
        allDataRows,
        infiniteQueryProps.fetchNextPage,
        callTriggerProps,
      ]
    );

    useLayoutEffect(() => {
      if (selectedIndex > -1 && selectedIndex < allDataRows.length) {
        const element = document.querySelector(`li[data-index="${selectedIndex}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, [allDataRows.length, selectedIndex]);

    return (
      <div>
        <div
          css={{
            padding: theme.spacing(0, 2, 2),
            display: 'grid',
            gap: theme.spacing(2),
            borderBottom: `1px solid ${theme.colors.neutral20}`,
          }}
        >
          {shouldShowSearch ? (
            <SearchBar
              label={t('Done')}
              onDismissClick={() => setShouldShowSearch(false)}
              field={searchField}
              searchHandler={(searchText) => {
                searchHandler(searchText);
              }}
            />
          ) : (
            <ActionBar
              selectedLocations={selectedLocationIds}
              onLocationChange={(locations) => {
                setFilters({
                  ...filters,
                  locationIds: locations,
                });
              }}
              onSearchClick={() => setShouldShowSearch(true)}
              showFilterBadge={hasFilterChanged(filters, defaultFilters)}
              showSearchBadge={!!searchField.value}
              filterProps={{
                hasFilterChanged,
                filters,
                resetFilters: () => {
                  setFilters(defaultFilters);
                },
                onChange: (filters) => {
                  setFilters(filters);
                },
                isMulti: selectedLocationIds.length > 1,
                phoneNumberOptions: {},
              }}
            />
          )}
          {/* <SwitchField {...switchField} label={t('View Only Missed/Abandoned Calls')} name='toggle' /> */}
        </div>
        <div>
          <InfinitePaginatedList
            height={listHeight}
            infiniteQueryProps={infiniteQueryProps}
            renderListItem={renderListItem}
          />
          {PhoneCallModal}
        </div>
      </div>
    );
  }
);
SmallView.displayName = 'HistorySmallView';

type PanelProps = {
  hasAccessToRecordings: boolean;
  callDetailsQueryKey: string[];
  sectionFocus: CallDetailsContext['sectionFocus'];
};

const getRenderListItem =
  ({
    getHistoryActions,
    triggerCall,
    setSelectedIndex,
    shouldShowLocationBadge,
    panelProps,
  }: // panelProps,
  {
    getHistoryActions: (props: HistoryActionsGetterProps) => Action[];
    triggerCall: (context: { personId: string; phoneNumber: string; outboundNumber: string }) => void;
    setSelectedIndex: (index: number) => void;
    shouldShowLocationBadge: boolean;
    panelProps: PanelProps & {
      rows: RecentCallRecord[];
      getNextPage: () => Promise<InfiniteQueryObserverResult<RecentCallRecords, unknown>>;
    };
  }) =>
  // eslint-disable-next-line react/display-name
  ({ listItem, index }: RenderListItemProps<RecentCallRecord>) => {
    return (
      <CallHistoryListItem
        listItem={listItem}
        index={index}
        getHistoryActions={getHistoryActions}
        triggerCall={triggerCall}
        setSelectedIndex={setSelectedIndex}
        shouldShowLocationBadge={shouldShowLocationBadge}
        panelProps={panelProps}
      />
    );
  };

const CallHistoryListItem = memo(
  ({
    listItem,
    index,
    shouldShowLocationBadge,
    getHistoryActions,
    triggerCall,
    setSelectedIndex,
    panelProps,
  }: {
    listItem: RecentCallRecord;
    index: number;
    shouldShowLocationBadge: boolean;
    getHistoryActions: (props: HistoryActionsGetterProps) => Action[];
    triggerCall: (context: { personId: string; phoneNumber: string; outboundNumber: string }) => void;
    setSelectedIndex: (index: number) => void;
    panelProps: PanelProps & {
      rows: RecentCallRecord[];
      getNextPage: () => Promise<InfiniteQueryObserverResult<RecentCallRecords, unknown>>;
    };
  }) => {
    const { t } = useTranslation('calls');
    const { getScopeName } = useAppScopeStore();
    const { setShow } = useSlidePanelShallowStore<CallDetailsContext>('setShow', 'panelType', 'show', 'context');

    const { actions }: { actions: Action[] } = useMemo(() => {
      const allActions = getHistoryActions({
        index,
        rowData: listItem,
        triggerCall: () => {
          triggerCall({
            personId: listItem.person?.personId ?? '',
            phoneNumber: (listItem.direction === 'inbound' ? listItem.callerNumber : listItem.dialedNumber) || '',
            outboundNumber: (listItem.direction === 'outbound' ? listItem.callerNumber : listItem.dialedNumber) || '',
          });
        },
        addTag: () => {
          // noop, since we're removing it anyway
        },
      }).filter((action) => action.id !== 'add-tag-action'); // Exclude add tag action from the list

      const showPanel = () =>
        setShow(true, 'callDetails', {
          ...panelProps,
          instanceId: 'table',
          currentRowId: PhonesUtils.getRecentCallsUniqueKey(listItem),
          toggleRowSelected: (_, _1, index) => {
            if (index === undefined) {
              return;
            }
            setSelectedIndex(index);
          },
        });

      allActions.unshift({
        id: 'view-details-action',
        label: 'View Call Details',
        Icon: () => getActionIcon({ iconName: 'document-filled' }),
        onClick: showPanel,
        trackingId: 'phone-portal-weave2.0-voicemails-view-details',
      });

      return { actions: allActions, markReadAction: allActions.find((action) => action.id === 'mark-read-action') };
    }, [getHistoryActions, index, listItem, triggerCall, setShow, panelProps, setSelectedIndex]);

    return (
      <div
        css={{
          display: 'grid',
          padding: theme.spacing(2),
          gridTemplateAreas: '"photo name actions" ". details details" ". meta meta"',
          gridTemplateColumns: 'auto 1fr auto',
          gridTemplateRows:
            '[first-start] auto [first-end] 0px [second-start] auto [second-end] 8px [third-start] auto [third-end]',
          columnGap: theme.spacing(1),
          alignItems: 'center',
          borderBottom: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <Photos.ContactProfilePhoto
          css={{ gridArea: 'photo', gridRow: 'first' }}
          locationId={listItem.locationId}
          personId={listItem.person?.personId || ''}
          name={PhonesUtils.getFullName(listItem.person?.firstName, listItem.person?.lastName)}
          disableClick={!listItem.person?.personId}
        >
          {/* {!listItem.readAt && <Avatar.Dot variant='primary' />} */}
        </Photos.ContactProfilePhoto>
        <NamePlate
          firstName={listItem.person?.firstName}
          lastName={listItem.person?.lastName}
          fallback={t('Unknown')}
          css={{ gridArea: 'name', gridRow: 'first' }}
          direction={listItem.direction as 'inbound' | 'outbound'}
        />

        {actions.length > 0 && <Actions actions={actions} />}
        <div
          css={{
            gridArea: 'details',
            display: 'grid',
            alignItems: 'center',
            gap: theme.spacing(1),
            gridRow: 'second',
          }}
        >
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: 'max-content max-content max-content',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}
          >
            <Text size='medium'>{dayjs(listItem.startedAt).format('MMM DD YYYY, hh:mm A')}</Text>•
            <Text size='medium'>
              {formatPhoneNumber(listItem.direction === 'inbound' ? listItem.callerNumber : listItem.dialedNumber)}
            </Text>
          </div>
        </div>
        <div
          css={{
            gridArea: 'meta',
            gridRow: 'third',
            display: 'grid',
            gridTemplateColumns: 'max-content max-content',
            alignItems: 'center',
            gap: theme.spacing(1),
          }}
        >
          {listItem.direction === 'inbound' && listItem.status && (
            <Chip css={{ width: 'max-content' }} variant={PhonesUtils.getStatusChipColor(listItem.status)}>
              {startCase(listItem.status)}
            </Chip>
          )}
          {shouldShowLocationBadge && listItem.locationId && (
            <Chips.LocationChip>{getScopeName(listItem.locationId)}</Chips.LocationChip>
          )}
        </div>
      </div>
    );
  }
);

CallHistoryListItem.displayName = 'CallHistoryListItem';

const Actions = ({ className, actions }: { className?: string; actions: Action[] }) => {
  // Separate call and message actions from the rest of the actions
  const { callAction, messageAction, remainingActions } = useMemo(
    () =>
      actions.reduce(
        (acc, action) => {
          if (action.id === 'call-action') {
            acc.callAction = action;
          } else if (action.id === 'message-action') {
            acc.messageAction = action;
          } else {
            acc.remainingActions.push(action);
          }
          return acc;
        },
        { callAction: undefined, messageAction: undefined, remainingActions: [] } as {
          callAction?: Action;
          messageAction?: Action;
          remainingActions: Action[];
        }
      ),
    [actions]
  );

  return (
    <div className={className} css={{ display: 'grid', gridTemplateColumns: 'repeat(3, max-content)' }}>
      {callAction && !callAction.hide && (
        <Button
          variant='secondary'
          iconName='phone'
          size='large'
          css={{ height: 32, width: 32 }}
          onClick={callAction.onClick}
          trackingId={callAction.trackingId}
          disabled={callAction.disabled}
        />
      )}
      {messageAction && !messageAction.hide && (
        <Button
          variant='secondary'
          iconName='messaging'
          size='large'
          css={{ height: 32, width: 32 }}
          onClick={messageAction.onClick}
          trackingId={messageAction.trackingId}
          disabled={messageAction.disabled}
        />
      )}

      <CustomActionCell actions={remainingActions} tableTrackingId={'phone-portal-weave2.0-voicemails'} />
    </div>
  );
};

const NamePlate = ({
  firstName,
  lastName,
  fallback,
  direction,
  className,
}: {
  firstName?: string;
  lastName?: string;
  fallback?: string;
  direction?: 'outbound' | 'inbound';
  className?: string;
}) => {
  const { t } = useTranslation('calls');
  let content;
  if (!firstName && !lastName) {
    content = (
      <Text as='span' size={'medium'}>
        {fallback || t('Unknown')}
      </Text>
    );
  } else {
    content = (
      <div>
        {!!firstName && (
          <Text as='span' size={'medium'} weight='bold'>
            {firstName}
          </Text>
        )}

        {firstName && lastName ? ' ' : null}

        {!!lastName && (
          <Text as='span' size={'medium'}>
            {lastName}
          </Text>
        )}
      </div>
    );
  }

  return (
    <div
      className={className}
      css={{
        display: 'grid',
        gridTemplateColumns: 'max-content max-content',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      {content}
      {direction === 'outbound' && <PhoneOutgoingIcon size={16} />}
    </div>
  );
};

const SearchBar = ({
  label,
  onDismissClick,
  field,
  searchHandler,
}: {
  label: string;
  onDismissClick: () => void;
  field: BaseFieldProps<string>;
  searchHandler: (searchText: string) => void;
}) => {
  return (
    <div css={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: theme.spacing(1) }}>
      <SearchField
        {...field}
        autoFocus
        onChange={(e) => {
          field.onChange(e);

          if (!e) {
            return;
          }

          if ('target' in e) {
            searchHandler((e.target as HTMLInputElement).value);
          } else {
            searchHandler(e.value);
          }
        }}
        label=''
        name='search'
      />
      <Button variant='secondary' size='large' onClick={onDismissClick}>
        {label}
      </Button>
    </div>
  );
};

const ActionBar = ({
  selectedLocations,
  onLocationChange,
  onSearchClick,
  showSearchBadge,
  showFilterBadge,
  filterProps,
}: {
  selectedLocations: string[];
  onLocationChange: (locations: string[]) => void;
  onSearchClick: () => void;
  showSearchBadge: boolean;
  showFilterBadge: boolean;
  filterProps: {
    hasFilterChanged: (incomingFilters: CallRecordsFiltersType, baselineFilters?: CallRecordsFiltersType) => boolean;
    filters: CallRecordsFiltersType;
    resetFilters: () => void;
    onChange: (filters: CallRecordsFiltersType) => void;
    isMulti: boolean;
    phoneNumberOptions: Record<
      string,
      {
        name: string;
        numbers: {
          value: string;
          label: string;
          name: string;
        }[];
      }
    >;
  };
}) => {
  const { modalProps, triggerProps } = useModalControl();

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gridTemplateAreas: '"location search filter"',
      }}
    >
      <ScopedLocationFilterMenu
        selectedOptions={selectedLocations}
        trackingIdBase={'phone-weave2.0-voicemails'}
        onChange={onLocationChange}
        showClearAll={false}
        css={{ gridArea: 'location' }}
      />
      <Button
        variant='secondary'
        iconName='search'
        size='large'
        css={[
          { gridArea: 'search' },
          css`
            ::after {
              position: absolute;
              opacity: ${showSearchBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        onClick={onSearchClick}
      />
      <Button
        variant='secondary'
        iconName='filter-alt'
        size='large'
        css={[
          { gridArea: 'filter' },
          css`
            ::after {
              position: absolute;
              opacity: ${showFilterBadge ? 1 : 0};
              background: ${theme.colors.primary50};
              border-radius: 16px;
              border: 1.5px solid ${theme.colors.white};
              height: ${theme.spacing(1.5)};
              width: ${theme.spacing(1.5)};
              top: 6px;
              left: ${theme.spacing(3)};
              z-index: ${theme.zIndex.high};
              content: '';
              transition: border-color, opacity ease-in-out 250ms;
            }
            :hover::after,
            :focus::after {
              border-color: ${theme.colors.neutral10};
            }
          `,
        ]}
        {...triggerProps}
      />
      <RecentCallsTrayFilter modalProps={modalProps} {...filterProps} />
    </div>
  );
};
